import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { NFL_START } from '@/lib/landingChain';

// SAB-409 / SAB-576: single source of truth for the cross-sport switch, shared by
// the mobile drawer (MobileHamburgerMenu) and the desktop profile menu
// (UserAccountMenu). Current sport is detected by route (/beta = MLB; every other
// authed surface is the NFL side); the switch target is always the INVERSE sport.

export type SportKey = 'mlb' | 'nfl';

export interface SportSwitchTarget {
  sport: SportKey;
  emoji: string;
  label: string;
  route: string;
}

export function currentSportFromPath(pathname: string): SportKey {
  return pathname.startsWith('/beta') ? 'mlb' : 'nfl';
}

export function inverseSportTarget(current: SportKey): SportSwitchTarget {
  return current === 'mlb'
    ? { sport: 'nfl', emoji: '🏈', label: 'NFL', route: '/nfl' }
    : { sport: 'mlb', emoji: '⚾', label: 'MLB', route: '/beta' };
}

/**
 * Where the switch lands you. The control is always visible now (see useSportSwitcher);
 * membership decides the DESTINATION, not whether you can see the switch — that was the
 * circular gate that hid the door to NFL from every MLB-only user.
 *
 *   has a league in the target sport -> that sport's shell (their league)
 *   NFL with no membership           -> /nfl/start, the join-or-create cold-start chooser
 *                                       (SAB-642), not a create-only dead end
 *   MLB with no membership           -> /beta, which renders its own no-league onboarding
 *
 * `null` (still loading the count) routes to the shell: /nfl and /beta both self-handle the
 * empty state, so a click during the load window is never a dead end, and it resolves to the
 * chooser once the count lands.
 */
export function switchDestination(target: SportSwitchTarget, hasTargetLeague: boolean | null): string {
  if (hasTargetLeague === false && target.sport === 'nfl') return NFL_START;
  return target.route;
}

/**
 * First-time framing: a member-less user is discovering the sport, not switching back to one
 * they already play, so "Try NFL" reads better than "Switch to NFL" for them. Members (and the
 * loading window) keep "Switch to <sport>".
 */
export function switchActionLabel(target: SportSwitchTarget, hasTargetLeague: boolean | null): string {
  return hasTargetLeague === false ? `Try ${target.label}` : `Switch to ${target.label}`;
}

/**
 * Returns the inverse-sport switch target plus whether the user has a league in that target
 * sport. The control is ALWAYS shown (callers no longer gate on membership — that hid the only
 * in-app path to a sport you had not joined). `hasTargetLeague` now feeds `destination` and
 * `actionLabel` instead. MLB and NFL are separate table sets: an MLB membership is a row in
 * `league_members`, an NFL membership a row in `nfl_league_members` (each read under its own
 * RLS "own rows" SELECT policy).
 *
 * hasTargetLeague: null while loading, then true/false.
 */
export function useSportSwitcher() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const current = currentSportFromPath(pathname);
  const target = inverseSportTarget(current);
  const [hasTargetLeague, setHasTargetLeague] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) {
      setHasTargetLeague(false);
      return;
    }
    let cancelled = false;
    const table = target.sport === 'mlb' ? 'league_members' : 'nfl_league_members';
    supabase
      .from(table as any)
      .select('league_id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .then(({ count }) => {
        if (!cancelled) setHasTargetLeague((count ?? 0) > 0);
      });
    return () => {
      cancelled = true;
    };
  }, [user, target.sport]);

  return {
    current,
    target,
    hasTargetLeague,
    destination: switchDestination(target, hasTargetLeague),
    actionLabel: switchActionLabel(target, hasTargetLeague),
  };
}
